import { useState, useEffect, useCallback } from 'react';
import NotificationService, { Notification } from '../services/NotificationService';

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  isLoading: boolean;
  refreshNotifications: () => Promise<void>;
  markAsRead: (id: string) => boolean;
  archiveNotification: (id: string) => boolean;
  isRead: (id: string) => boolean;
  markAllAsRead: () => boolean;
}

export const useNotifications = (clientId: string | undefined): UseNotificationsReturn => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // ── Bootstrap: run once when clientId is available ──────────────────────────
  useEffect(() => {
    if (!clientId || initialized) return;

    setIsLoading(true);
    NotificationService.initialize(clientId)
      .then(() => {
        setInitialized(true);
        setNotifications(NotificationService.getNotifications());
        setUnreadCount(NotificationService.getUnreadCount());
      })
      .finally(() => setIsLoading(false));
  }, [clientId, initialized]);

  // ── Event listeners: purely reactive, zero polling ──────────────────────────
  useEffect(() => {
    const onNotificationsUpdated = (updated: Notification[]) => {
      setNotifications([...updated]);
      setUnreadCount(NotificationService.getUnreadCount());
    };
    const onConnectionStatus = (status: boolean) => setIsConnected(status);
    const onNotificationRead  = () => setUnreadCount(NotificationService.getUnreadCount());

    NotificationService.on('notifications-updated', onNotificationsUpdated);
    NotificationService.on('connection-status',     onConnectionStatus);
    NotificationService.on('notification-read',     onNotificationRead);

    return () => {
      NotificationService.removeListener('notifications-updated', onNotificationsUpdated);
      NotificationService.removeListener('connection-status',     onConnectionStatus);
      NotificationService.removeListener('notification-read',     onNotificationRead);
    };
  }, []);

  // ── Manual refresh: REST only, WebSocket is untouched ───────────────────────
  const refreshNotifications = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await NotificationService.refreshFromRest();
      setNotifications([...NotificationService.getNotifications()]);
      setUnreadCount(NotificationService.getUnreadCount());
    } finally {
      setIsLoading(false);
    }
  };

  return {
    notifications,
    unreadCount,
    isLoading,
    isConnected,
    refreshNotifications,
    markAsRead:          useCallback((id: string) => NotificationService.markAsRead(id), []),
    archiveNotification: useCallback((id: string) => NotificationService.archiveNotification(id), []),
    isRead:              useCallback((id: string) => NotificationService.isRead(id), []),
    markAllAsRead:       useCallback(() => NotificationService.markAllAsRead(), []),
  };
};

export default useNotifications;

